import React, { useState, useEffect, useRef } from 'react';
import {
	Box, Card, Typography, IconButton, TextField, Dialog, DialogContent, DialogActions, DialogTitle, Button, Tooltip,
	Grid, Avatar, MenuItem, useMediaQuery, useTheme, Checkbox, Chip, InputAdornment, DialogContentText,
	Slider
} from '@mui/material';
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from 'react-router-dom';
import MedicalServicesIcon from "@mui/icons-material/MedicalServices"; // doctor icon
import { DataGrid } from "@mui/x-data-grid";
import { checkPhone, checkEmailExists, createCustomer, deleteCustomers, getAllCustomers, getCustomerById, updateCustomer } from '../services/customers';
import { getDoctors } from '../services/doctors';
import { createCustomerPostvital, getCustomerSessionDetails, getCustomerSessionDetailsByCustomerId } from '../services/customerSessionDetails';
import InsightsIcon from '@mui/icons-material/Insights';
import Analytics from '@mui/icons-material/Analytics';
import userLogo from "../assets/user-logo.svg";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import SearchIcon from "@mui/icons-material/Search";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { getEffectiveAccountId } from '../utils/accountId';


const Patients = () => {
	const navigate = useNavigate();
	const rawAccountId = localStorage.getItem("account_id");
	// Accounts 4/5 behave like accounts 2/3; rawAccountId stays the real id for API calls.
	const accountId = getEffectiveAccountId(rawAccountId);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
	const isAmruthaAccount = accountId === "3";
	const isArcAccount = rawAccountId === "4" || rawAccountId === "5";
	const defaultHospital = rawAccountId === "4"
		? "ARC Chemotherapy"
		: rawAccountId === "5"
			? "ARC Radiotherapy"
			: isAmruthaAccount ? "Amrutha Hospital" : "AIG";

	const hadsQualifyingMin = isArcAccount ? 1 : 8;




	const [openDialog, setOpenDialog] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState(null);
	const [customers, setCustomers] = useState([]);
	const [selectedIds, setSelectedIds] = useState([]);
	const [doctors, setDoctors] = useState([]);
	const [openHistory, setOpenHistory] = useState(false);
	const [selectedCustomerId, setSelectedCustomerId] = useState(null);
	const [sessionHistory, setSessionHistory] = useState([]);
	const [sessionDetails, setSessionDetails] = useState(null);
	const [sessionId, setSessionId] = useState(null);
	const [expandedId, setExpandedId] = useState(null);
	const [loadingDetails, setLoadingDetails] = useState(false);
	const [assignmentRole, setAssignmentRole] = useState("");
	const [preferredLanguage, setPreferredLanguage] = useState("");
	const [customerName, setCustomerName] = useState("");
	const [patient, setPatient] = useState(null);
	const [isInsightsComingSoonOpen, setIsInsightsComingSoonOpen] = useState(false);
	const [openPostVital, setOpenPostVital] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [phoneExists, setPhoneExists] = useState(false);
	const [emailExists, setEmailExists] = useState(false);
	const [openPopup, setOpenPopup] = useState(false);
	const [currentDate, setCurrentDate] = useState("");

	const emptyForm = {
		account_id: Number(rawAccountId),
		code: "",
		package_type: "",
		firstname: "",
		lastname: "",
		date_of_birth: "",
		age: "",
		gender: "",
		email: "",
		phone: "",
		assigned_doctor: "",
		diagnosis: "",
		primary_diagnosis: "",
		stage: "",
		date_of_diagnosis: "",
		treatment_type: "",
		treatment_start_date: "",
		no_of_cycles_planned: "",
		current_cycle: "",
		hospital: defaultHospital,
		preferred_language: "",
		hads: false,
		assignment_role: "",
		consented: 0,
		hads_date: "",
		other_diagnosis_type: "",
		other_treatment_type: "",
		date_of_birth: "",
	};

	const [formData, setFormData] = useState(emptyForm);

	// Date components for mobile-friendly date selection
	const [dobMonth, setDobMonth] = useState('');
	const [dobDay, setDobDay] = useState('');
	const [dobYear, setDobYear] = useState('');




	const fetchCustomers = async (query = "") => {
		try {
			if (!rawAccountId) {
				console.error("Account ID not found in localStorage");
				return;
			}

			const data = await getAllCustomers(rawAccountId, query);
			setCustomers(data.customers || []);
		} catch (err) {
			console.log(err, "Failed to fetch customers");
		}
	};

	useEffect(() => {
		const delay = setTimeout(() => {
			fetchCustomers(searchTerm);
		}, 400); // debounce 400ms

		return () => clearTimeout(delay);
	}, [searchTerm]);




	useEffect(() => {
		if (!openHistory || !selectedCustomerId) return;

		const fetchHistory = async () => {
			try {
				const data = await getCustomerSessionDetailsByCustomerId(selectedCustomerId);
				setSessionHistory(data);
			} catch (err) {
				console.error("Failed to load history", err);
			}
		};

		fetchHistory();
	}, [openHistory, selectedCustomerId]);

	const fetchSessionDetails = async () => {
		if (!sessionId) return;

		setLoadingDetails(true);
		try {
			const data = await getCustomerSessionDetails(sessionId);
			setSessionDetails(data);
		} catch (error) {
			console.error("Failed to fetch session details:", error);
		}
		setLoadingDetails(false);
	};

	useEffect(() => {
		fetchSessionDetails();
	}, [sessionId]);

	const moodRatingMap = {
		happy: "😊 Happy",
		sad: "😞 Sad",
		angry: "😡 Angry",
		disgust: "🤢 Disgust",
		surprised: "😲 Surprised",
		bad_low: "😔 Bad / Low",
		none: "❓ None of the above",
	};

	const sleepQualityMap = {
		very_poor: "😫 Very Poor",
		poor: "😴 Poor",
		fair: "😌 Fair",
		good: "😊 Good",
		excellent: "🌟 Excellent",
	};

	const languageMap = {
		eng: "English",
		hin: "Hindi",
		tel: "Telugu",
		ben: "Bengali",
		kan: "Kannada",
	};

	const displayLanguage = languageMap[preferredLanguage] || preferredLanguage || "-";

	const assessments =
		accountId === "1"
			? [
				{
					title: "HADS Assessment",
					subtitle: "Hospital Anxiety and Depression Scale",
					key: "hads",
				},
				{
					title: "FACT-G7 Assessment",
					subtitle: "Functional Assessment of Cancer Therapy",
					key: "factg7",
				},
				{
					title: "EORTC QLQ-C30",
					subtitle: "European Quality of Life Questionnaire",
					key: "eortc",
				},
			]
			: [
				{
					title: "HADS Assessment",
					subtitle: "Hospital Anxiety and Depression Scale",
					key: "hads",
				},
				{
					title: "PSQI Assessment",
					subtitle: "Sleep Quality measurement",
					key: "psqi",
				},
				{
					title: "EORTC QLQ-C30",
					subtitle: "European Quality of Life Questionnaire",
					key: "eortc",
				},
			];

	const postVitals = [];
	if (sessionDetails?.post_blood_pressure) {
		postVitals.push(`Blood Pressure - ${sessionDetails.post_blood_pressure} mmHg`);
	}
	if (sessionDetails?.post_heart_rate) {
		postVitals.push(`Heart Rate - ${sessionDetails.post_heart_rate} bpm`);
	}
	if (sessionDetails?.post_pain_scale) {
		postVitals.push(`Pain Scale - ${sessionDetails.post_pain_scale}/10`);
	}

	const fields = sessionDetails
		? [
			{ label: "Treatment Cycle", value: sessionDetails.treatment_cycle },
			{
				label: "Blood Pressure",
				value: sessionDetails.blood_pressure
					? `${sessionDetails.blood_pressure} mmHg`
					: "-",
			},
			{
				label: "Heart Rate",
				value: sessionDetails.heart_rate
					? `${sessionDetails.heart_rate} bpm`
					: "-",
			},
			{
				label: "Pain Scale",
				value: sessionDetails.pain_scale
					? `${sessionDetails.pain_scale}/10`
					: "-",
			},
			{
				label: "Mood Rating",
				value: moodRatingMap[sessionDetails.mood_rating] || "-",
			},
			{ label: "Nausea", value: sessionDetails.nausea || "-" },
			{ label: "Fatigue", value: sessionDetails.fatigue || "-" },
			{ label: "Anxiety", value: sessionDetails.anxiety || "-" },
			{ label: "Depression", value: sessionDetails.depression || "-" },
			{ label: "Appetite Loss", value: sessionDetails.appetite_loss || "-" },
			{
				label: "Sleep Quality",
				value: sleepQualityMap[sessionDetails.sleep_quality] || "-",
			},
			{
				label: "Suggested Care Soundscape",
				value: sessionDetails.track_displayName || "-",
			},
			{
				label: "Assessment Type",
				value: sessionDetails.assessment_type
					? sessionDetails.assessment_type
						.map((item) => {
							const match = assessments.find((a) => a.key === item.title);
							const title = match ? match.title : item.title;

							// Attach scores
							if (title === "HADS Assessment" && sessionDetails.hads_score) {
								return `${title} - ${sessionDetails.hads_score}`;
							}
							if (title === "EORTC QLQ-C30" && sessionDetails.eortc_score) {
								return `${title} - ${sessionDetails.eortc_score}`;
							}
							if (title === "PSQI Assessment" && sessionDetails.psqi_score) {
								return `${title} - ${sessionDetails.psqi_score}`;
							}

							return title;
						})
						.join(", ")
					: "-",
			},
			...(postVitals.length > 0
				? [{ label: "Post Vital", value: postVitals.join(", ") }]
				: []),

		]
		: [];

	const resetForm = () => {
		setSelectedTemplate(null);
		setFormData({ ...emptyForm });
		setDobMonth('');
		setDobDay('');
		setDobYear('');
		setPhoneExists(false);
		setEmailExists(false);
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
		resetForm();
	};

	const hasPostVitals = (session) => Boolean(
		session?.post_blood_pressure ||
		session?.post_heart_rate ||
		session?.post_pain_scale
	);

	const getSessionTime = (session) => new Date(session?.updated_at || session?.created_at || 0).getTime();

	const isCompletedSession = (session) => session?.status?.toLowerCase() === "completed";

	const findLatestCompletedSession = (sessions = []) => sessions
		.filter(isCompletedSession)
		.sort((current, next) => getSessionTime(next) - getSessionTime(current))[0];

	const getLatestCompletedSessionDetails = async (sessions = []) => {
		const latestFirst = [...sessions].sort((current, next) => {
			const timeDifference = getSessionTime(next) - getSessionTime(current);
			if (timeDifference !== 0) return timeDifference;
			return (next?.id || 0) - (current?.id || 0);
		});

		for (const session of latestFirst) {
			const details = await getCustomerSessionDetails(session.id);
			if (isCompletedSession(details)) return details;
		}

		return null;
	};

	const handleOpenLatestPostVital = async (customer, event) => {
		event.stopPropagation();
		try {
			const history = await getCustomerSessionDetailsByCustomerId(customer.id);
			const latestCompletedSession = await getLatestCompletedSessionDetails(history);

			if (!latestCompletedSession) {
				alert("No completed session found for this patient.");
				return;
			}

			if (hasPostVitals(latestCompletedSession)) {
				alert("Post vital is already submitted for the latest completed session.");
				return;
			}

			setSelectedCustomerId(customer.id);
			setSessionId(latestCompletedSession.id);
			setSessionDetails(latestCompletedSession);
			setOpenPostVital(true);
		} catch (error) {
			console.error("Failed to open post vital for latest completed session:", error);
			alert("Unable to load the latest completed session. Please try again.");
		}
	};


	const openCreatePatient = () => {
		resetForm();              // clear everything
		setSelectedTemplate(null);
		setSelectedCustomerId(null);

		// FIX: always reset currentDate to today when creating a new patient
		const today = new Date();
		const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
		setCurrentDate(todayStr);

		setOpenDialog(true);
	};

	const openEditPatient = (row) => {
		setSelectedTemplate(row);       // mark EDIT mode
		setSelectedCustomerId(row.id);
		setFormData({
			...emptyForm,
			...row,
			hospital: defaultHospital,
		});

		// FIX: show the patient's real treatment_start_date when editing.
		// Fall back to today (never an empty string) so the required-field
		// check on the Update button doesn't get stuck disabled.
		const today = new Date();
		const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
		setCurrentDate(row.treatment_start_date ? row.treatment_start_date.split('T')[0] : todayStr);

		setPreferredLanguage(row.preferred_language);
		setCustomerName(`${row.firstname} ${row.lastname}`);
		setOpenDialog(true);
	};

	useEffect(() => {
		if (dobYear && dobMonth && dobDay) {
			const dateString = `${dobYear}-${dobMonth.padStart(2, '0')}-${dobDay.padStart(2, '0')}`;
			setFormData((prev) => ({
				...prev,
				date_of_birth: dateString,
			}));
		} else {
			setFormData((prev) => ({
				...prev,
				date_of_birth: '',
			}));
		}
	}, [dobYear, dobMonth, dobDay]);

	const handleRowClick = (row) => {
		setSelectedCustomerId(row.id);
		setPreferredLanguage(row.preferred_language);
		setCustomerName(`${row.firstname} ${row.lastname}`);

		setTimeout(() => {
			setOpenHistory(true);
		}, 50);
	};

	const handleChange = async (e) => {
		const { name, value } = e.target;

		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));

		// Sync currentDate with treatment_start_date
		if (name === "treatment_start_date") {
			setCurrentDate(value);
		}

		// Check phone only
		if (name === "phone") {
			if (/^[6-9]\d{9}$/.test(value)) {
				try {
					const res = await checkPhone({
						phone: value,
						account_id: Number(rawAccountId),
					});

					// ⬇️ SHOW ALERT WHEN EXISTS
					if (res.success === false || res.exists === true) {
						alert(res.message || "Phone number already exists");
						setPhoneExists(true);
						return;
					}

					setPhoneExists(false);

				} catch (error) {
					console.error("Error checking phone:", error);

					// ⬇️ Backend error handling
					if (error.message) {
						alert(error.message);
					}
				}
			} else {
				setPhoneExists(false);
			}
		}

		// Check email only
		if (name === "email") {
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (emailRegex.test(value)) {
				try {
					const res = await checkEmailExists(value, rawAccountId);

					// ⬇️ SHOW ALERT WHEN EXISTS
					if (res.success === false) {
						alert(res.message || "Email already exists");
						setEmailExists(true);
						return;
					}

					setEmailExists(false);

				} catch (error) {
					console.error("Error checking email:", error);

					// ⬇️ Backend error handling
					if (error.message) {
						alert(error.message);
					}
				}
			} else {
				setEmailExists(false);
			}
		}
	}

	// Handle date component changes for mobile-friendly date selection
	const handleDateComponentChange = (component, value) => {
		let newMonth = component === 'month' ? value : dobMonth;
		let newDay = component === 'day' ? value : dobDay;
		let newYear = component === 'year' ? value : dobYear;

		if (component === 'month') setDobMonth(value);
		if (component === 'day') setDobDay(value);
		if (component === 'year') setDobYear(value);

		// If all components are filled, create the date string
		if (newMonth && newDay && newYear) {
			const dateString = `${newYear}-${String(newMonth).padStart(2, '0')}-${String(newDay).padStart(2, '0')}`;
			setFormData((prev) => ({
				...prev,
				date_of_birth: dateString,
			}));
		} else {
			// 🔥 CLEAR DOB if any part is missing
			setFormData((prev) => ({
				...prev,
				date_of_birth: '',
			}));
		}

	};

	const fetchDoctors = async () => {
		try {
			const data = await getDoctors(rawAccountId);
			const activeDoctors = data.filter(
				(doctor) => doctor.status?.toLowerCase() === "active"
			);
			setDoctors(activeDoctors);
		} catch (err) {
			console.error("Failed to load doctors:", err);
		}
	};

	useEffect(() => {
		fetchDoctors();
	}, [rawAccountId]);

	// Populate date components when formData.date_of_birth changes
	useEffect(() => {
		if (formData.date_of_birth) {
			const date = new Date(formData.date_of_birth);
			if (!isNaN(date.getTime())) {
				setDobMonth(String(date.getMonth() + 1));
				setDobDay(String(date.getDate()));
				setDobYear(String(date.getFullYear()));
			}
		}
	}, [formData.date_of_birth]);

	const fetchCustomer = async () => {
		if (selectedCustomerId) {
			try {
				const data = await getCustomerById(selectedCustomerId, rawAccountId);

				setPatient(data);

				console.log("Fetched patient:", data);
			} catch (error) {
				console.error(error);
			}
		}
	};

	useEffect(() => {
		fetchCustomer();
	}, [selectedCustomerId]);

	const handleSubmit = async () => {
		try {

			const { firstname, gender, phone, preferred_language, assigned_doctor, anxiety_score, depression_score, consented } = formData;

			if (!firstname?.trim() || !gender?.trim() || !phone?.trim() || !preferred_language?.trim()) {
				let missing = [];
				if (!firstname?.trim()) missing.push("First Name");
				if (!gender?.trim()) missing.push("Gender");
				if (!phone?.trim()) missing.push("Phone");
				if (!preferred_language?.trim()) missing.push("Preferred Language");
				if (!assigned_doctor) missing.push("Assigned Doctor");

				alert(`Please fill the following required field(s): ${missing.join(", ")}`);
				return;
			}

			// ⭐ MOBILE NUMBER VALIDATION (Indian format)
			const phoneRegex = /^[6-9]\d{9}$/;
			if (!phoneRegex.test(phone)) {
				alert("Please enter a valid 10-digit mobile number.");
				return;
			}

			let payload = {};

			if (selectedTemplate) {
				Object.keys(formData).forEach((key) => {
					const newValue = formData[key];
					const oldValue = selectedTemplate[key];

					if (typeof oldValue === 'number') {
						if (Number(newValue) !== oldValue) payload[key] = Number(newValue);
						// 🔥 FIX for booleans — do NOT overwrite unless changed
					} else if (typeof oldValue === "boolean") {
						// Always send old value if unchanged (because backend bool cannot be null)
						payload[key] = newValue;
					}
					// normal fields
					else if (newValue !== oldValue) {
						payload[key] = newValue;
					}
				});

				// Ensure numeric fields are properly converted in update mode
				["age", "no_of_cycles_planned", "current_cycle", "assigned_doctor"].forEach((key) => {
					if (payload[key] !== undefined && payload[key] !== null && payload[key] !== "") {
						payload[key] = Number(payload[key]);
					}
				});

				// FIX: treatment_start_date is displayed via `currentDate`, not
				// `formData.treatment_start_date`, so the diff loop above never
				// picks up changes to it. Sync it explicitly here — this also
				// backfills patients whose stored treatment_start_date was null.
				const normalizedOldStartDate = selectedTemplate.treatment_start_date
					? selectedTemplate.treatment_start_date.split('T')[0]
					: "";
				if (currentDate && currentDate !== normalizedOldStartDate) {
					payload.treatment_start_date = currentDate;
				}

				console.log("Updating patient with changed fields:", payload);


				if (Object.keys(payload).length > 0) {
					await updateCustomer(selectedTemplate.id, payload, rawAccountId);
					alert("Patient updated successfully!");
				} else {
					console.log("No changes detected, skipping update.");
					alert("No changes to update.");
				}
					} else {
				payload = { ...formData };

				// Ensure treatment_start_date is set from currentDate
				if (currentDate && !payload.treatment_start_date) {
					payload.treatment_start_date = currentDate;
				}

				["age", "no_of_cycles_planned", "current_cycle", "assigned_doctor"].forEach((key) => {
					if (payload[key] === "" || payload[key] === null) {
						payload[key] = null;
					} else {
						payload[key] = Number(payload[key]);
					}
				});

				["hads_date", "treatment_start_date", "date_of_diagnosis", "date_of_birth"].forEach((key) => {
					if (!payload[key]) payload[key] = null;
				});

				payload.consented = accountId === "1";

				console.log("Creating patient:", payload);

				await createCustomer(payload);
				alert("Patient created successfully!");
			}
			setOpenDialog(false);
			resetForm();
			fetchCustomers();
			fetchCustomer();
		} catch (error) {
			console.error("Error submitting patient:", error);

			// Axios error response handling
			if (error.response && error.response.data && error.response.data.error) {
				alert(error.response.data.error);
			} else {
				alert("Failed to submit patient!");
			}
		}
	};

	const handleDeleteSelected = async () => {
		if (!selectedIds.length) return alert("No customer selected");

		if (!window.confirm("Are you sure you want to delete the selected patient(s)?")) return;

		try {
			for (const id of selectedIds) {
				await deleteCustomers(id, rawAccountId);
			}
			fetchCustomers();
			alert("Selected customer(s) deleted successfully");
		} catch (err) {
			console.error("Error deleting customer:", err);
			alert(err.message || "Failed to delete customer(s)");
		}
	};

	const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email || "");

	const formatDateTime = (dateString) => {
		if (!dateString) return "N/A";

		const date = new Date(dateString);
		if (isNaN(date.getTime())) return "N/A";

		return date.toLocaleString("en-IN", {
			day: "2-digit",
			month: "short",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			hour12: true,
			timeZone: "UTC"
		});
	};

	const columns = [
		{
			field: "name",
			headerName: "Name",
			flex: 1.5,
			minWidth: 180,
			renderCell: (params) => {
				const firstName = params.row.firstname || "";
				const lastName = params.row.lastname || "";
				const fullName = `${firstName} ${lastName}`.trim();
				const { gender, date_of_birth, phone, email, age } = params.row;

				const calculateAge = (dob) => {
					if (!dob) return null;
					const birthDate = new Date(dob);
					const today = new Date();
					let calculatedAge = today.getFullYear() - birthDate.getFullYear();
					const monthDiff = today.getMonth() - birthDate.getMonth();
					if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
						calculatedAge--;
					}
					return calculatedAge;
				};

				const displayAge = age || calculateAge(date_of_birth);
				const genderAge = gender && displayAge
					? `${gender.charAt(0).toUpperCase() + gender.slice(1)} - ${displayAge} years`
					: gender
						? gender.charAt(0).toUpperCase() + gender.slice(1)
						: displayAge
							? `${displayAge} years`
							: "—";

				return (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "flex-start",
							height: "100%",
							minWidth: 0,
						}}
					>
						<Avatar
							sx={{
								width: 30,
								height: 30,
								bgcolor: "primary.main",
								mr: 1.5,
								color: "white",
								fontSize: "1rem",
								fontWeight: 500,
							}}
						>
							{fullName
								.split(" ")
								.map((n) => n[0])
								.join("")
								.toUpperCase()}
						</Avatar>
						<Box sx={{ minWidth: 0 }}>
							<Typography
								variant="subtitle2"
								sx={{
									fontWeight: 500,
									color: "#374151",
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
							>
								{fullName}
							</Typography>
							<Typography
								variant="body2"
								sx={{
									fontWeight: 500,
									color: "#111827",
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
							>
								{genderAge}
							</Typography>
						</Box>
					</Box>
				);
			},
		},
		{
			field: "personal_info",
			headerName: "Personal Info",
			flex: 2,
			minWidth: 200,
			renderCell: (params) => {
				const { gender, date_of_birth, phone, email, age } = params.row;

				const calculateAge = (dob) => {
					if (!dob) return null;
					const birthDate = new Date(dob);
					const today = new Date();
					let calculatedAge = today.getFullYear() - birthDate.getFullYear();
					const monthDiff = today.getMonth() - birthDate.getMonth();
					if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
						calculatedAge--;
					}
					return calculatedAge;
				};

				const displayAge = age || calculateAge(date_of_birth);
				const genderAge = gender && displayAge
					? `${gender.charAt(0).toUpperCase() + gender.slice(1)} - ${displayAge} years`
					: gender
						? gender.charAt(0).toUpperCase() + gender.slice(1)
						: displayAge
							? `${displayAge} years`
							: "—";

				return (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							height: "100%",
							py: 0.5,
						}}
					>
						{/* Phone */}
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
							<PhoneIcon sx={{ fontSize: "1rem", color: "#4b5563" }} />
							<Typography variant="body2" sx={{ color: "#4b5563", fontSize: "0.8rem" }}>
								{phone || "—"}
							</Typography>
						</Box>

						{/* Email */}
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
							<EmailIcon sx={{ fontSize: "1rem", color: "#4b5563" }} />
							<Typography variant="body2" sx={{ color: "#4b5563", fontSize: "0.8rem" }}>
								{email || "—"}
							</Typography>
						</Box>
					</Box>

				);
			},
		},
		{
			field: "assigned_doctor_name",
			headerName: "Assigned Doctor",
			flex: 1.5,
			minWidth: 150,
			renderCell: (params) => (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						height: "100%",
					}}
				>
					<Typography>{params.value || "—"}</Typography>
				</Box>
			),
		},
		{
			field: "progress_tracking",
			headerName: "Progress Tracking",
			flex: 1.5,
			minWidth: 200,
			renderCell: (params) => {
				const { gender, date_of_birth, treatment_cycle, treatment_arm, age, updated_at, } = params.row;

				const calculateAge = (dob) => {
					if (!dob) return null;
					const birthDate = new Date(dob);
					const today = new Date();
					let calculatedAge = today.getFullYear() - birthDate.getFullYear();
					const monthDiff = today.getMonth() - birthDate.getMonth();
					if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
						calculatedAge--;
					}
					return calculatedAge;
				};

				const displayAge = age || calculateAge(date_of_birth);
				const genderAge = gender && displayAge
					? `${gender.charAt(0).toUpperCase() + gender.slice(1)} - ${displayAge} years`
					: gender
						? gender.charAt(0).toUpperCase() + gender.slice(1)
						: displayAge
							? `${displayAge} years`
							: "—";

				return (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							height: "100%",
							py: 0.5,
							gap: 0.5,
						}}
					>
						{treatment_cycle > 0 && (
							<Typography
								variant="body2"
								sx={{
									fontWeight: 500,
									color: "#111827",
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
							>
								Current Cycle - {treatment_cycle + 1}
							</Typography>
						)}
						{treatment_cycle > 0 && (
							<Typography
								variant="body2"
								sx={{
									fontWeight: 500,
									color: "#111827",
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
							>
								Session Completed - {treatment_cycle}
							</Typography>
						)}
						<Typography
							variant="body2"
							sx={{
								fontWeight: 500,
								color: "#111827",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
								display: "flex",
								alignItems: "center",
								gap: "6px",
							}}
						>
							<AccessTimeIcon sx={{ fontSize: 16, color: "#555" }} />
							{formatDateTime(updated_at)}
						</Typography>


						{params.row.treatment_arm && (
							<Box display="flex" alignItems="center" gap={1}>
								{(accountId === "2" || accountId === "3") && params.row.treatment_arm && (
									<Box
										sx={{
											backgroundColor:
												params.row.treatment_arm === "test"
													? "#4caf50"
													: params.row.treatment_arm === "control"
														? "#fbc02d"
														: "#9e9e9e",
											color: "white",
											fontWeight: 600,
											px: 1.5,
											py: 0.3,
											borderRadius: "8px",
											fontSize: "0.75rem",
											display: "inline-block",
											textTransform: "capitalize",
										}}
									>
										{params.row.treatment_arm === "test"
											? "Test Arm"
											: params.row.treatment_arm === "control"
												? "Control Arm"
												: "N/A"}
									</Box>
								)}
							</Box>
						)}



					</Box>
				);
			},
		},
		{
			field: "actions",
			headerName: "Actions",
			flex: 1.25,
			minWidth: 220,
			sortable: false,
			filterable: false,
			renderCell: (params) => {
				const { id, hads, consented, assignment_role, age, treatment_cycle, depression_score, anxiety_score } = params.row;

				return (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
								justifyContent: "center",
							height: "100%",
							width: "100%",
						}}
					>
						<Box sx={{ display: "flex", flexDirection: "column", alignItems: "stretch", width: "100%", maxWidth: 190 }}>
							{/* MAIN BUTTON */}
							{/* If depression/anxiety < 7 AND hads is true → show Not Qualified button */}
							{(accountId === "2" && (depression_score < hadsQualifyingMin || anxiety_score < hadsQualifyingMin)) && hads ? (
								<Button
									variant="contained"
									// color="error"
									size="small"
									sx={{
										width: "100%",
										height: 32,
										borderRadius: "8px",
										textTransform: "none",
										fontWeight: 600,
										backgroundColor: "#2e7d32",
										"&:hover": {
											backgroundColor: "#1b5e20",
										},
									}}
									onClick={(e) => {
										e.stopPropagation();
										alert(
											"It’s wonderful to see that you are managing your anxiety and depression well. Keep up the great work and continue your positive progress."
										);
									}}
								>
									Not Qualified
								</Button>
							) : (
								/* Otherwise show your original button */
								<Button
									variant="contained"
									size="small"
									style={{
										background: (() => {
											if ((accountId === "2" || accountId === "3") && age < 18) return "#d32f2f";
											if ((accountId === "2" || accountId === "3") && (hads && !consented)) return "#ff9800";
											if (accountId === "2" && (depression_score < hadsQualifyingMin || anxiety_score < hadsQualifyingMin)) return "#2e7d32";
											if ((accountId === "2" || accountId === "3") && !hads) return "#2e7d32";
											return "#1976d2";
										})(),
										color: "white",
									}}
									sx={{
										width: "100%",
										height: 32,
										borderRadius: "8px",
										textTransform: "none",
										fontWeight: 600,
										"&:hover": {
											background: (accountId === "2" || accountId === "3") && age < 18
												? "#9a0007 !important"
												: (accountId === "2" || accountId === "3") && (hads && !consented)
													? "#f57c00 !important"
													: (accountId === "2" && (depression_score < hadsQualifyingMin || anxiety_score < hadsQualifyingMin)) || ((accountId === "2" || accountId === "3") && !hads)
														? "#1b5e20 !important"
														: "#115293 !important",
										},
									}}
									onClick={async (e) => {
										e.stopPropagation();

										if ((accountId === "2" || accountId === "3") && age < 18) {
											alert("You are not eligible as you are under 18.");
											return;
										}

										if ((accountId === "2" && (depression_score < hadsQualifyingMin || anxiety_score < hadsQualifyingMin)) && hads) {
											alert("It’s wonderful to see that you are managing your anxiety and depression well. Keep up the great work and continue your positive progress.");
											return;
										}

										if ((accountId === "2" || accountId === "3") && (hads && !consented)) {
											alert("As you have not provided consent for this treatment, we will not be proceeding further.");
											return;
										}

										if (accountId === "2" || accountId === "3") {
											if (consented) {
												navigate("/treatmentsessions", { state: { customer: params.row } });
												return;
											}
											if (hads) {
												navigate("/prehadsscreening", { state: { customer: params.row } });
												return;
											}
											navigate("/prehadsscreening", { state: { customer: params.row } });
										} else if (accountId === "1") {
											navigate("/treatmentsessions", { state: { customer: params.row } });
										}
									}}
								>
									{accountId === "1"
										? "Start Session"
										: (accountId === "2" || accountId === "3")
											? age < 18
												? "Under Age"
												: (hads && !consented)
													? "Not Consented"
													: hads
														? "Start Session"
														: "Pre-HADS Screening"
											: ""}
								</Button>
							)}

							{treatment_cycle > 0 && (
								<Box sx={{ display: "flex", gap: 0.75, mt: 0.75, width: "100%" }}>
{(accountId === "1" || accountId === "2" || accountId === "3" || isArcAccount) && (										<Button
											variant="contained"
											size="small"
											sx={{
												flex: 1,
												minWidth: 0,
												height: 30,
												borderRadius: "8px",
												color: "white",
												backgroundColor: "#7C9BFF",
												boxShadow: "0px 3px 10px rgba(124, 155, 255, 0.35)",
												fontSize: "0.75rem",
												fontWeight: 600,
												textTransform: "none",
												whiteSpace: "nowrap",
												"&:hover": {
													backgroundColor: "#6688F0",
													boxShadow: "0px 4px 12px rgba(102, 136, 240, 0.4)",
												},
											}}
											onClick={(event) => handleOpenLatestPostVital(params.row, event)}
										>
											Post Vital
										</Button>
									)}

									<Box
										sx={{
										flex: 1,
										minWidth: 0,
										display: "inline-flex",
										alignItems: "center",
										justifyContent: "center",
										background: "linear-gradient(135deg, #FF8A50 0%, #FF7043 100%)",
										color: "white",
										fontWeight: 600,
										fontSize: "0.75rem",
										height: 30,
										borderRadius: "8px",
										textTransform: "none",
										cursor: "pointer",
										transition: "all 0.2s ease",
										whiteSpace: "nowrap",
										gap: 0.6,
										boxShadow: "0px 3px 10px rgba(255, 112, 67, 0.35)",
										"&:hover": {
											background: "linear-gradient(135deg, #FF7043 0%, #F4511E 100%)",
											boxShadow: "0px 4px 12px rgba(244, 81, 30, 0.4)",
										},
									}}
									onClick={(e) => {
										e.stopPropagation();
										e.nativeEvent.stopImmediatePropagation();
										navigate("/analytics", { state: { customerId: id, accountId: rawAccountId } });
									}}
								>
									<Analytics sx={{ fontSize: "1rem", color: "white" }} />
									Analytics
								</Box>
							</Box>
						)}
						</Box>
					</Box >
				);
			},
		}

	];

	// Convert status to camelCase
	const formatStatus = (str) =>
		str
			?.toLowerCase()
			.split(" ")
			.map((word, index) =>
				index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
			)
			.join("");

	// Color mapping
	const statusColorMap = {
		active: "#b2f2bb",       // light green
		inProgress: "#69db7c",   // mid dark green
		completed: "#38a169",    // dark green
		expired: "#9e9e9e",      // gray
	};

	const camelStatus = formatStatus(sessionDetails?.status);
	const chipColor = statusColorMap[camelStatus] || "#9e9e9e";
	const isLatestCompletedPatientCycle = Number(sessionDetails?.treatment_cycle) === Number(patient?.treatment_cycle);

	useEffect(() => {
		// Get today's date and format as YYYY-MM-DD
		const today = new Date();
		const yyyy = today.getFullYear();
		const mm = String(today.getMonth() + 1).padStart(2, "0"); // Months are 0-based
		const dd = String(today.getDate()).padStart(2, "0");
		setCurrentDate(`${yyyy}-${mm}-${dd}`);
	}, []);

	const handleClosePostVital = () => {
		setFormData(prev => ({
			...prev,
			systolic: "",
			diastolic: "",
			bloodPressure: "",
			heartRate: "",
			painScale: 0,
		}));

		setOpenPostVital(false);
	};


	return (

		<Box sx={{ p: { xs: 0, sm: 2, md: 3 }, width: '100%', overflowX: 'hidden' }}>

			<Box
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', sm: 'row' },
					justifyContent: { xs: 'flex-start', sm: 'space-between' },
					alignItems: { xs: 'flex-start', sm: 'center' },
					width: '100%',
					mb: { xs: 2, sm: 3 },
					px: { xs: 2, sm: 0 },
					gap: { xs: 2, sm: 0 },
					overflow: 'visible'
				}}
			>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: { xs: 1, sm: 2 },
						flexWrap: 'wrap',
						width: { xs: '100%', sm: 'auto' },
						justifyContent: { xs: 'flex-start', sm: 'flex-end' },
						overflow: 'visible'
					}}
				>
					<Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' }, display: "flex", gap: 2, mb: { xs: 1, sm: 0 } }}>
						<Typography
							variant="h6"
							fontWeight={400}
							sx={{
								color: "#1f2937",
								mb: 0,
								fontSize: { xs: '1.1rem', sm: '1.25rem' },
								whiteSpace: 'nowrap'
							}}
						>
							Registered Patients
						</Typography>
					</Box>

<Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 1.5 }, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' }, alignItems: 'center' }}>						<Button
							variant="contained"
							startIcon={<AddIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
							onClick={openCreatePatient}
							sx={{
								bgcolor: "#4A90E2",
								"&:hover": { bgcolor: "#357ABD" },
								borderRadius: 2,
								px: { xs: 1.5, sm: 2.5 },
								py: { xs: 0.8, sm: 1 },
								textTransform: "none",
								fontWeight: 600,
								fontSize: { xs: '0.8rem', sm: '0.875rem' },
								boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
								whiteSpace: "nowrap",
								minWidth: { xs: 100, sm: 'auto' },
								height: { xs: 36, sm: 'auto' },
								flex: { xs: '1 1 auto', sm: '0 0 auto' }
							}}
						>
							Add Patient
						</Button>

						{storedUser?.user_type === "admin" && (<Tooltip title="Delete Patients" arrow>
							<Button
								variant="outlined"
								startIcon={isMobile ? null : <DeleteIcon sx={{ fontSize: 20 }} />}
								onClick={handleDeleteSelected}
								sx={{
									borderColor: '#f44336',
									color: '#f44336',
									borderRadius: 2,
									px: { xs: 1, sm: 2.5 },
									py: { xs: 0.8, sm: 1 },
									textTransform: 'none',
									fontWeight: 600,
									fontSize: { xs: '0.8rem', sm: '0.875rem' },
									height: { xs: 36, sm: 'auto' },
									minWidth: { xs: 44, sm: 'auto' },
flex: { xs: '0 0 auto', sm: '0 0 auto' },
ml: { xs: 'auto', sm: 0 },
									'&:hover': {
										borderColor: '#d32f2f',
										bgcolor: 'rgba(244, 67, 54, 0.04)'
									}
								}}
							>
								{isMobile ? <DeleteIcon sx={{ fontSize: 20 }} /> : 'Delete Patients'}
							</Button>
						</Tooltip>)}

						<Button
							variant="contained"
							onClick={() => navigate("/todays-patient")}
							sx={{
								bgcolor: "#4A90E2",
								"&:hover": { bgcolor: "#357ABD" },
								borderRadius: 2,
								px: { xs: 1.5, sm: 2.5 },
								py: { xs: 0.8, sm: 1 },
								textTransform: "none",
								fontWeight: 600,
								fontSize: { xs: '0.8rem', sm: '0.875rem' },
								boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
								whiteSpace: "nowrap",
								minWidth: { xs: 100, sm: 'auto' },
								height: { xs: 36, sm: 'auto' },
								flex: { xs: '1 1 auto', sm: '0 0 auto' },
							}}
						>
							Today's Patient
						</Button>
										<Tooltip title="Doctors" arrow>
							<IconButton
								onClick={() => navigate('/doctors')}
								sx={{
									display: { xs: 'inline-flex', sm: 'none' },
									bgcolor: '#f5f5f5',
									color: '#666',
									borderRadius: 2,
									width: 36,
									height: 36,
									flex: '0 0 auto',
									'&:hover': {
										bgcolor: '#e0e0e0',
										color: '#333'
									}
								}}
							>
								<MedicalServicesIcon sx={{ fontSize: 20 }} />
							</IconButton>
						</Tooltip>

					</Box>
							
				</Box>

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: { xs: 1, sm: 2 },
						flexWrap: 'wrap',
						width: { xs: '100%', sm: 'auto' },
						justifyContent: { xs: 'flex-start', sm: 'flex-end' },
						overflow: 'visible'
					}}
				>
					<Tooltip title="Doctors" arrow>
						<IconButton
							onClick={() => navigate('/doctors')}
							sx={{
								display: { xs: 'none', sm: 'inline-flex' },
								bgcolor: '#f5f5f5',
								color: '#666',
								borderRadius: 2,
								width: { xs: 36, sm: 40 },
							}}
						>
							<MedicalServicesIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
						</IconButton>
					</Tooltip>
				</Box>
			</Box>

			<Box
				sx={{
					flex: { xs: '1 1 100%', sm: '0 0 auto' },
					minWidth: { xs: '100%', sm: 240 },
					width: { xs: '100%', sm: 'auto' }
				}}
			>
				<TextField
					placeholder="Search name, email, phone"
					variant="outlined"
					size="small"
					fullWidth
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon sx={{ color: "#777" }} />
							</InputAdornment>
						),
					}}
					sx={{
						backgroundColor: '#fff',
						borderRadius: 2,
						'& .MuiOutlinedInput-root': {
							borderRadius: 2,
							height: { xs: 40, sm: 40 }
						},
						'& .MuiInputBase-input::placeholder': {
							color: '#777',
							opacity: 1,
							fontSize: { xs: '0.85rem', sm: '0.95rem' }
						},
					}}
				/>
			</Box>


			<Box sx={{ py: 2, width: '100%', }}>
				{/* Mobile Card View */}
				{isMobile ? (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 2 }}>
						{customers.map((customer) => {
							const firstName = customer.firstname || "";
							const lastName = customer.lastname || "";
							const fullName = `${firstName} ${lastName}`.trim();
							const { gender, phone, email, age, id, hads, consented, treatment_arm, depression_score, anxiety_score, treatment_cycle, updated_at } = customer;

							const displayAge = age;
							const genderAge = gender && displayAge
								? `${gender.charAt(0).toUpperCase() + gender.slice(1)} - ${displayAge} years`
								: gender
									? gender.charAt(0).toUpperCase() + gender.slice(1)
									: displayAge
										? `${displayAge} years`
										: "—";

							return (
								<Card
									key={id}
									sx={{
										borderRadius: 3,
										boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
										border: '1px solid #e5e7eb',
										overflow: 'hidden',
										transition: 'all 0.2s ease',
										'&:active': {
											transform: 'scale(0.98)',
											boxShadow: '0 1px 6px rgba(0,0,0,0.1)',
										}
									}}
								>
									{/* Card Header */}
									<Box
										sx={{
											bgcolor: '#4a90e2',
											p: 1.5,
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between'
										}}
									>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
											<Checkbox
												checked={selectedIds.includes(id)}
												onChange={(e) => {
													e.stopPropagation();
													if (e.target.checked) {
														setSelectedIds([...selectedIds, id]);
													} else {
														setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
													}
												}}
												sx={{
													color: 'white',
													padding: '8px',
													'&.Mui-checked': {
														color: 'white',
													},
												}}
											/>
											<Avatar
												sx={{
													width: 40,
													height: 40,
													bgcolor: 'white',
													color: '#4a90e2',
													fontSize: '1rem',
													fontWeight: 600,
												}}
											>
												{fullName
													.split(" ")
													.map((n) => n[0])
													.join("")
													.toUpperCase()}
											</Avatar>
											<Box sx={{ flex: 1, minWidth: 0 }}>
												<Typography
													variant="h6"
													sx={{
														color: 'white',
														fontWeight: 600,
														fontSize: '1rem',
														whiteSpace: 'nowrap',
														overflow: 'hidden',
														textOverflow: 'ellipsis',
													}}
												>
													{fullName}
												</Typography>
											</Box>
										</Box>
									</Box>

									{/* Card Body */}
									<Box
										sx={{ p: 1.5 }}
										onClick={() => handleRowClick(customer)}
									>
										{/* Personal Info */}
										<Box sx={{ mb: 1.5 }}>
											<Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
												Personal Information
											</Typography>
											<Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
													<Typography variant="body2" sx={{ color: '#374151', fontWeight: 500 }}>
														{genderAge}
													</Typography>
												</Box>
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
													<Typography variant="body2" sx={{ color: '#6b7280' }}>📞</Typography>
													<Typography variant="body2" sx={{ color: '#374151' }}>
														{phone || "—"}
													</Typography>
												</Box>
												{email && (
													<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
														<Typography variant="body2" sx={{ color: '#6b7280' }}>✉️</Typography>
														<Typography variant="body2" sx={{ color: '#374151', wordBreak: 'break-all' }}>
															{email}
														</Typography>
													</Box>
												)}
											</Box>
										</Box>

										{/* Doctor Info */}
										{customer.assigned_doctor_name && (
											<Box sx={{ mb: 1.5 }}>
												<Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
													Assigned Doctor
												</Typography>
												<Typography variant="body2" sx={{ color: '#374151', mt: 0.5 }}>
													{customer.assigned_doctor_name}
												</Typography>
											</Box>
										)}

										<Box
											sx={{
												display: "flex",
												flexDirection: "column",
												justifyContent: "center",
												height: "100%",
												py: 0.5,
												gap: 0.5,
												mb: 1
											}}
										>
											{treatment_cycle > 0 && (
												<Typography
													variant="body2"
													sx={{
														fontWeight: 500,
														color: "#111827",
														whiteSpace: "nowrap",
														overflow: "hidden",
														textOverflow: "ellipsis",
													}}
												>
													Current Cycle - {treatment_cycle + 1}
												</Typography>
											)}
											{treatment_cycle > 0 && (
												<Typography
													variant="body2"
													sx={{
														fontWeight: 500,
														color: "#111827",
														whiteSpace: "nowrap",
														overflow: "hidden",
														textOverflow: "ellipsis",
													}}
												>
													Session Completed - {treatment_cycle}
												</Typography>
											)}
											<Typography
												variant="body2"
												sx={{
													fontWeight: 500,
													color: "#111827",
													whiteSpace: "nowrap",
													overflow: "hidden",
													textOverflow: "ellipsis",
													display: "flex",
													alignItems: "center",
													gap: "6px",
												}}
											>
												<AccessTimeIcon sx={{ fontSize: 16, color: "#555" }} />
												{formatDateTime(updated_at)}
											</Typography>
										</Box>

										<Box
    sx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1,
    }}
>
    {/* Assignment Role Badge */}
    {(accountId === "2" || accountId === "3") && treatment_arm && (
        <Chip
            label={treatment_arm === "test" ? "Test Arm" : treatment_arm === "control" ? "Control Arm" : "N/A"}
            sx={{
                backgroundColor:
                    treatment_arm === "test"
                        ? "#4caf50"
                        : treatment_arm === "control"
                            ? "#fbc02d"
                            : "#9e9e9e",
                color: 'white',
                fontWeight: 600,
                fontSize: '0.75rem',
                borderRadius: "8px",
                height: 32,
                flexShrink: 0,
            }}
        />
    )}

    {/* MAIN BUTTON */}
{(accountId === "1" || accountId === "2" || accountId === "3" || isArcAccount) && treatment_cycle > 0 && (		<Button
			variant="contained"
			size="small"
			sx={{
				backgroundColor: "#4A90E2",
				whiteSpace: "nowrap",
				flexShrink: 0,
				height: 32,
				fontSize: "0.75rem",
				"&:hover": { backgroundColor: "#357ABD" },
			}}
			onClick={(event) => handleOpenLatestPostVital(customer, event)}
		>
			Post Vital
		</Button>
	)}

    {(accountId === "2" && (depression_score < hadsQualifyingMin || anxiety_score < hadsQualifyingMin)) && hads ? (
        <Button
            variant="contained"
            size="small"
            sx={{
                backgroundColor: "#2e7d32",
                whiteSpace: "nowrap",
                flexShrink: 0,
                height: 32,
                fontSize: "0.75rem",
                "&:hover": { backgroundColor: "#1b5e20" },
            }}
            onClick={(e) => {
                e.stopPropagation();
                alert("It's wonderful to see that you are managing your anxiety and depression well. Keep up the great work and continue your positive progress.");
            }}
        >
            Not Qualified
        </Button>
    ) : (
        <Button
            variant="contained"
            size="small"
            style={{
                background: (() => {
                    if ((accountId === "2" || accountId === "3") && age < 18) return "#d32f2f";
                    if ((accountId === "2" || accountId === "3") && (hads && !consented)) return "#ff9800";
                    if (accountId === "2" && (depression_score < hadsQualifyingMin || anxiety_score < hadsQualifyingMin)) return "#2e7d32";
                    if ((accountId === "2" || accountId === "3") && !hads) return "#2e7d32";
                    return "#1976d2";
                })(),
                color: "white",
            }}
            sx={{
                whiteSpace: "nowrap",
                flexShrink: 0,
                height: 32,
                fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                px: 1.5,
                "&:hover": {
                    background: (accountId === "2" || accountId === "3") && age < 18
                        ? "#9a0007 !important"
                        : (accountId === "2" || accountId === "3") && (hads && !consented)
                            ? "#f57c00 !important"
                            : (accountId === "2" && (depression_score < hadsQualifyingMin || anxiety_score < hadsQualifyingMin)) || ((accountId === "2" || accountId === "3") && !hads)
                                ? "#1b5e20 !important"
                                : "#115293 !important",
                },
            }}
            onClick={async (e) => {
                e.stopPropagation();
                if ((accountId === "2" || accountId === "3") && age < 18) {
                    alert("You are not eligible as you are under 18.");
                    return;
                }
                if ((accountId === "2" && (depression_score < hadsQualifyingMin || anxiety_score < hadsQualifyingMin)) && hads) {
                    alert("It's wonderful to see that you are managing your anxiety and depression well. Keep up the great work and continue your positive progress.");
                    return;
                }
                if ((accountId === "2" || accountId === "3") && (hads && !consented)) {
                    alert("As you have not provided consent for this treatment, we will not be proceeding further.");
                    return;
                }
                if (accountId === "2" || accountId === "3") {
                    if (consented) {
                        navigate("/treatmentsessions", { state: { customer: customer } });
                        return;
                    }
                    if (hads) {
                        navigate("/prehadsscreening", { state: { customer: customer } });
                        return;
                    }
                    navigate("/prehadsscreening", { state: { customer: customer } });
                } else if (accountId === "1") {
                    navigate("/treatmentsessions", { state: { customer: customer } });
                }
            }}
        >
            {accountId === "1"
                ? "Start Session"
                : (accountId === "2" || accountId === "3")
                    ? age < 18
                        ? "Under Age"
                        : (hads && !consented)
                            ? "Not Consented"
                            : hads
                                ? "Start Session"
                                : "Pre-HADS Screening"
                    : ""}
        </Button>
    )}

    {/* VIEW HISTORY BADGE */}
    {treatment_cycle > 0 && (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #FF8A50 0%, #FF7043 100%)",
                color: "white",
                fontWeight: 600,
                fontSize: "0.75rem",
                px: 1.5,
                height: 32,
                borderRadius: "10px",
                textTransform: "none",
                cursor: "pointer",
                transition: "all 0.25s ease",
                boxShadow: "0px 3px 10px rgba(255, 112, 67, 0.35)",
                whiteSpace: "nowrap",
                flexShrink: 0,
                gap: 0.8,
                "&:hover": {
                    background: "linear-gradient(135deg, #FF7043 0%, #F4511E 100%)",
                },
            }}
            onClick={(e) => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                navigate("/analytics", { state: { customerId: id, accountId: rawAccountId } });
            }}
        >
            <Analytics sx={{ fontSize: "1rem", color: "white" }} />
            Analytics
        </Box>
    )}
</Box>
									</Box>
								</Card>
							);
						})}
					</Box>
				) : (
					<Box sx={{
						width: '100%',
						overflowX: 'auto',
						overflowY: 'visible',
						WebkitOverflowScrolling: 'touch',
						'@media (max-width: 1024px)': {
							width: '100%',
							maxWidth: '100%',
						},
						'@media (max-width: 768px)': {
							width: '100vw',
							marginLeft: '-1rem',
							marginRight: '-1rem',
							paddingLeft: '1rem',
							paddingRight: '1rem',
						},
						'&::-webkit-scrollbar': {
							height: { xs: '12px', sm: '8px' },
							display: 'block',
						},
						'&::-webkit-scrollbar-track': {
							background: '#f1f1f1',
							borderRadius: '4px',
						},
						'&::-webkit-scrollbar-thumb': {
							background: '#4a90e2',
							borderRadius: '4px',
							border: '1px solid #f1f1f1',
						},
						'&::-webkit-scrollbar-thumb:hover': {
							background: '#357ABD',
						}
					}}>
						<DataGrid
							rows={customers}
							columns={columns}
							getRowId={(row) => row.id}
							pageSize={10}
							rowsPerPageOptions={[10, 20, 50]}
							checkboxSelection
							disableRowSelectionOnClick
							onRowClick={(params) => handleRowClick(params.row)}
							autoHeight
							getRowHeight={() => 'auto'}
							selectionModel={selectedIds}
							onRowSelectionModelChange={(selectionModel) => {
								const idsArray = Array.from(selectionModel.ids);
								console.log("Selected IDs Array:", idsArray);
								setSelectedIds(idsArray);
							}}
							disableColumnResize={false}
							sx={{
								border: 'none',
								borderRadius: 2,
								boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
								minWidth: '1200px',
								width: '100%',
								'& .clickable-row': {
									cursor: 'pointer',
								},
								'& .MuiDataGrid-root': {
									border: 'none',
									minWidth: '1200px',
								},
								'& .MuiDataGrid-main': {
									minWidth: '1200px',
								},
								'& .MuiDataGrid-virtualScroller': {
									minWidth: '1200px',
								},
								'@media (max-width: 768px)': {
									minWidth: '1200px',
									'& .MuiDataGrid-root': {
										minWidth: '1200px',
									},
									'& .MuiDataGrid-main': {
										minWidth: '1200px',
										overflowX: 'auto',
									},
									'& .MuiDataGrid-virtualScroller': {
										minWidth: '1200px',
										overflowX: 'auto',
									},
									'& .MuiDataGrid-columnHeaders': {
										minWidth: '1200px',
									},
								},
								'@media (min-width: 769px) and (max-width: 1024px)': {
									'& .MuiDataGrid-columnHeader[data-field="reference"]': {
										display: 'none',
									},
									'& .MuiDataGrid-cell[data-field="reference"]': {
										display: 'none',
									},
								},
								'& .MuiDataGrid-columnHeaders': {
									minHeight: '48px !important',
									maxHeight: '48px !important',
								},
								'& .MuiDataGrid-cell': {
									fontSize: '0.95rem',
									paddingTop: '12px',
									paddingBottom: '12px',
									borderRight: 'none !important',
									'&:focus': {
										outline: 'none !important',
										border: 'none !important',
									},
									'&:focus-within': {
										outline: 'none !important',
										border: 'none !important',
									},
									'&.Mui-selected': {
										backgroundColor: 'transparent !important',
										border: 'none !important',
									},
								},
								'& .MuiDataGrid-columnHeaderTitle': {
									fontSize: '0.8rem',
									whiteSpace: 'normal',
									lineHeight: '1.2',
									textAlign: 'left',
								},
								'& .MuiDataGrid-columnHeader': {
									backgroundColor: '#4a90e2',
									color: '#ffffff',
									fontWeight: 600,
									fontSize: '0.9rem',
									'&:focus, &:focus-within': {
										outline: 'none',
									},
									padding: '0 8px',
								},
								'& .MuiDataGrid-columnSeparator': {
									color: 'rgba(255, 255, 255, 0.3)',
								},
								'& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIcon': {
									color: '#ffffff !important',
								},
								'& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon': {
									color: '#ffffff !important',
								},
								'& .MuiDataGrid-columnHeader .MuiIconButton': {
									color: '#ffffff !important',
									'& svg': {
										color: '#ffffff !important',
										fill: '#ffffff !important',
									},
								},
								'& .MuiDataGrid-columnHeaderTitleContainer .MuiIconButton': {
									color: '#ffffff !important',
									'& svg': {
										color: '#ffffff !important',
										fill: '#ffffff !important',
									},
								},
								'& .MuiDataGrid-columnHeaders .MuiIconButton svg': {
									color: '#ffffff !important',
									fill: '#ffffff !important',
								},
								'& .MuiDataGrid-columnHeaders .MuiSvgIcon-root': {
									color: '#ffffff !important',
									fill: '#ffffff !important',
								},
								'& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root': {
									color: '#ffffff !important',
									transform: 'scale(0.8)',
									'& .MuiSvgIcon-root': {
										fontSize: '1.2rem',
									},
									'&.Mui-checked': {
										color: '#ffffff !important',
									},
								},
								'& .MuiDataGrid-cellCheckbox .MuiCheckbox-root': {
									transform: 'scale(0.8)',
									'& .MuiSvgIcon-root': {
										fontSize: '1.2rem',
									},
								},
								'& .MuiDataGrid-cell--withRenderer': {
									border: 'none !important',
									outline: 'none !important',
								},
								'& .MuiDataGrid-cell.MuiDataGrid-cell--editing': {
									border: 'none !important',
									outline: 'none !important',
								},
								'& .MuiDataGrid-row': {
									'&:hover': {
										backgroundColor: 'rgba(74, 144, 226, 0.05)',
										cursor: 'pointer',
									},
									'&.Mui-selected': {
										backgroundColor: 'rgba(74, 144, 226, 0.1)',
										'&:hover': {
											backgroundColor: 'rgba(74, 144, 226, 0.15)',
										},
									},
								},
							}}
						/>
					</Box>
				)}
			</Box>

			<Dialog
				open={openDialog}
				onClose={handleCloseDialog}
				maxWidth="lg"
				fullWidth
				fullScreen={isMobile}
				sx={{
					'& .MuiDialog-paper': {
						margin: { xs: 0, sm: 2 },
						maxHeight: { xs: '100%', sm: 'calc(100% - 64px)' }
					}
				}}
			>
				<DialogTitle>
					<Box sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}>
						<Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
							{selectedTemplate ? 'Edit Patient' : 'Create Patient'}
						</Typography>
					</Box>
				</DialogTitle>

				<DialogContent>
					<Box sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 0 } }}>
						<Grid container spacing={{ xs: 2, sm: 3 }}>
							<Grid item width="100%">
								<Typography
									variant="h6"
									sx={{
										fontSize: '1rem',
										color: 'gray',
										fontWeight: 500
									}}
								>
									Personal Information
								</Typography>
							</Grid>

							<Grid size={{ xs: 12, sm: 4 }}>
								<TextField
									fullWidth
									required
									label="First Name"
									name="firstname"
									value={formData.firstname}
									onChange={handleChange}
									InputLabelProps={{ shrink: true }}
								/>
							</Grid>

						<Grid size={{ xs: 12, sm: 4 }}>
								<TextField
									fullWidth
									label="Last Name"
									name="lastname"
									value={formData.lastname}
									onChange={handleChange}
									InputLabelProps={{ shrink: true }}
								/>
							</Grid>


							{/* Date of Birth - Mobile-friendly dropdowns */}
							<Grid size={{ xs: 12, sm: 4 }}>
								<Typography variant="caption" sx={{ color: '#666', mb: 0.5, display: 'block' }}>
									Date Of Birth *
								</Typography>
								<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
									<TextField
										select
										required
										label="Month"
										value={dobMonth}
										onChange={(e) => handleDateComponentChange('month', e.target.value)}
										sx={{ flex: '1 1 30%', minWidth: { xs: '65px', sm: '80px' }, ".MuiInputBase-root": { height: '32px' } }}
										InputLabelProps={{ shrink: true }}
									>
										<MenuItem value="">Month</MenuItem>
										<MenuItem value="1">Jan</MenuItem>
										<MenuItem value="2">Feb</MenuItem>
										<MenuItem value="3">Mar</MenuItem>
										<MenuItem value="4">Apr</MenuItem>
										<MenuItem value="5">May</MenuItem>
										<MenuItem value="6">Jun</MenuItem>
										<MenuItem value="7">Jul</MenuItem>
										<MenuItem value="8">Aug</MenuItem>
										<MenuItem value="9">Sep</MenuItem>
										<MenuItem value="10">Oct</MenuItem>
										<MenuItem value="11">Nov</MenuItem>
										<MenuItem value="12">Dec</MenuItem>
									</TextField>
									<TextField
										select
										required
										label="Day"
										value={dobDay}
										onChange={(e) => handleDateComponentChange('day', e.target.value)}
										sx={{ flex: 1, minWidth: '80px', ".MuiInputBase-root": { height: '32px' } }}
										InputLabelProps={{ shrink: true }}
									>
										<MenuItem value="">Day</MenuItem>
										{Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
											<MenuItem key={day} value={String(day)}>{day}</MenuItem>
										))}
									</TextField>
									<TextField
										select
										required
										label="Year"
										value={dobYear}
										onChange={(e) => handleDateComponentChange('year', e.target.value)}
										sx={{ flex: 1, minWidth: '90px', ".MuiInputBase-root": { height: '32px' } }}
										InputLabelProps={{ shrink: true }}
									>
										<MenuItem value="">Year</MenuItem>
										{Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i).map((year) => (
											<MenuItem key={year} value={String(year)}>{year}</MenuItem>
										))}
									</TextField>
								</Box>
							</Grid>

							<Grid size={{ xs: 12, sm: 4 }}>
								<TextField
									fullWidth
									select
									required
									label="Gender"
									defaultValue=""
									name="gender"
									value={formData.gender}
									onChange={handleChange}
									InputLabelProps={{ shrink: true }}
									
								>
									<MenuItem value="male">Male</MenuItem>
									<MenuItem value="female">Female</MenuItem>
									<MenuItem value="others">Others</MenuItem>
								</TextField>
							</Grid>

							<Grid size={{ xs: 12, sm: 4 }}>
								<TextField
									fullWidth
									required
									label="Email"
									name="email"
									value={formData.email}
									onChange={handleChange}
									InputLabelProps={{ shrink: true }}
									error={
										(formData.email && !isValidEmail) || emailExists
									}
									helperText={
										emailExists
											? "This email already exists!"
											: formData.email && !isValidEmail
												? "Enter a valid email address"
												: ""
									}
								/>
							</Grid>

						<Grid size={{ xs: 12, sm: 4 }}>
								<TextField
									fullWidth
									label="Phone"
									name="phone"
									required
									value={formData.phone}
									onChange={handleChange}
									InputLabelProps={{ shrink: true }}
									error={
										(formData.phone && !/^[6-9]\d{9}$/.test(formData.phone)) || phoneExists
									}
									helperText={
										phoneExists
											? "This phone number already exists!"
											: formData.phone && !/^[6-9]\d{9}$/.test(formData.phone)
												? "Enter a valid 10-digit mobile number"
												: ""
									}
								/>
							</Grid>



														<Grid size={{ xs: 12, sm: 6 }}>
								<TextField
									fullWidth
									select
									required
									label="Preferred Language"
									name="preferred_language"
									value={formData.preferred_language}
									onChange={handleChange}
									InputLabelProps={{ shrink: true }}
									
								>
									<MenuItem value="eng">English</MenuItem>
									<MenuItem value="hin">Hindi</MenuItem>
									{/* {accountId !== "3" && <MenuItem value="tel">Telugu</MenuItem>} */}
									{accountId !== "3" && rawAccountId !== "5" && <MenuItem value="ben">Bengali</MenuItem>}
									{(accountId === "3" || rawAccountId === "5") && <MenuItem value="kan">Kannada</MenuItem>}
								</TextField>
							</Grid>

							{!selectedTemplate && accountId === "1" && (
								<Grid size={{ xs: 12, sm: 6 }}>
									<TextField
										fullWidth
										select
										label="Package Type"
										name="package_type"
										value={formData.package_type}
										onChange={handleChange}
										InputLabelProps={{ shrink: true }}
										
									>
										<MenuItem value="basic">Basic</MenuItem>
										<MenuItem value="premium">Premium</MenuItem>
										<MenuItem value="advanced">Advanced</MenuItem>
									</TextField>
								</Grid>
							)}

							<Grid item xs={12} sx={{ width: '100%', minWidth: 0 }}>
								<Typography
									variant="h6"
									sx={{
										fontSize: '1rem',
										color: 'gray',
										fontWeight: 500
									}}
								>
									Medical Information
								</Typography>
							</Grid>
<Grid size={{ xs: 12, sm: 6 }}>
								<TextField
    fullWidth
    select
    required
    label="Assigned Doctor"
    name="assigned_doctor"
    value={formData.assigned_doctor || ""}
    onChange={handleChange}
>
									{doctors.map((doc) => (
										<MenuItem key={doc.id} value={doc.id}>
											{doc.name}
										</MenuItem>
									))}
								</TextField>
							</Grid>

						<Grid size={{ xs: 12, sm: 6 }}>
								<TextField
									fullWidth
									label="Diagnosis"
									name="diagnosis"
									value={formData.diagnosis}
									onChange={handleChange}
									InputLabelProps={{ shrink: true }}
								/>
							</Grid>

							<Grid item width="100%">
								<Typography
									variant="h6"
									sx={{
										fontSize: '1rem',
										color: 'gray',
										fontWeight: 500
									}}
								>
									Medical Information
								</Typography>
							</Grid>

							<Grid size={{ xs: 12, sm: 4 }}>

								<TextField
									fullWidth
									label="Primary Diagnosis"
									select
									InputLabelProps={{ shrink: true }}
									defaultValue=""
									name="primary_diagnosis"
									value={formData.primary_diagnosis}
									onChange={handleChange}
									
								>
									<MenuItem value="breast-cancer">Breast Cancer</MenuItem>
									<MenuItem value="lung_cancer">Lung Cancer</MenuItem>
									<MenuItem value="colorectal_cancer">Colorectal Cancer</MenuItem>
									<MenuItem value="prostate-cancer">Prostate Cancer</MenuItem>
									<MenuItem value="lymphoma">Lymphoma</MenuItem>
									<MenuItem value="lukemia">Lukemia</MenuItem>
									<MenuItem value="other-cancers">Other Cancers</MenuItem>
									<MenuItem value="others">Others</MenuItem>
								</TextField>
							</Grid>

							{formData.primary_diagnosis === "others" && (
							<Grid size={{ xs: 12, sm: 4 }}>
									<TextField
										fullWidth
										label="Other Diagnosis Type"
										name="other_diagnosis_type"
										value={formData.other_diagnosis_type}
										onChange={handleChange}
										InputLabelProps={{ shrink: true }}
									/>
								</Grid>
							)}

							{formData.primary_diagnosis !== "others" && (
							<Grid size={{ xs: 12, sm: 4 }}>
									<TextField
										fullWidth
										label="Stage"
										InputLabelProps={{ shrink: true }}
										select
										defaultValue=""
										name="stage"
										value={formData.stage}
										onChange={handleChange}
										
									>
										<MenuItem value="stage1">Stage 1</MenuItem>
										<MenuItem value="stage2">Stage 2</MenuItem>
										<MenuItem value="stage3">Stage 3</MenuItem>
										<MenuItem value="stage4">Stage 4</MenuItem>
									</TextField>
								</Grid>
							)}

						<Grid size={{ xs: 12, sm: 4 }}>
								<TextField
									fullWidth
									label="Date of Diagnosis"
									type="date"
									name="date_of_diagnosis"
									value={formData.date_of_diagnosis ? formData.date_of_diagnosis.split('T')[0] : ''}
									onChange={handleChange}
									InputLabelProps={{ shrink: true }}
									inputProps={{
										max: new Date().toISOString().split('T')[0],
										style: { fontSize: '16px' }
									}}
									placeholder="MM/DD/YYYY"
									sx={{
										width: { xs: '100%', sm: '250px' },
										'& input[type="date"]::-webkit-calendar-picker-indicator': {
											cursor: 'pointer',
											fontSize: '1.2rem',
											padding: '4px'
										},
										'& input[type="date"]': {
											fontSize: { xs: '16px', sm: '1rem' },
											cursor: 'pointer'
										}
									}}
								/>
							</Grid>

							<Grid size={{ xs: 12, sm: 4 }}>
								<TextField
									fullWidth
									label="Treatment Type"
									InputLabelProps={{ shrink: true }}
									select
									defaultValue=""
									name="treatment_type"
									value={formData.treatment_type}
									onChange={handleChange}
								
								>
									<MenuItem value="chemotherapy">Chemotherapy</MenuItem>
									<MenuItem value="radiation">Radiation</MenuItem>
									<MenuItem value="chemo_radiation">Chemo + Radiation</MenuItem>
									<MenuItem value="immunotherapy">Immunotherapy</MenuItem>
									<MenuItem value="surgery">Surgery</MenuItem>
									<MenuItem value="combination_therapy">Combination Therapy</MenuItem>
									<MenuItem value="others">Others</MenuItem>
								</TextField>
							</Grid>

							{formData.treatment_type === "others" && (
								<Grid size={{ xs: 12, sm: 4 }}>
									<TextField
										fullWidth
										label="Other Treatment Type"
										name="other_treatment_type"
										value={formData.other_treatment_type}
										onChange={handleChange}
										InputLabelProps={{ shrink: true }}
									/>
								</Grid>
							)}

							<Grid size={{ xs: 12, sm: 4 }}>
								<TextField
									fullWidth
									required
									label="Treatment Start Date"
									type="date"
									name="treatment_start_date"
									value={currentDate}
									onChange={handleChange}
									InputLabelProps={{ shrink: true }}
									inputProps={{
										style: { fontSize: '16px' },
										readOnly: true,
									}}
									placeholder="MM/DD/YYYY"
									sx={{
										width: { xs: "100%", sm: "250px" },
										"& input[type=date]::-webkit-calendar-picker-indicator": {
											cursor: "not-allowed",
											fontSize: "1.2rem",
											padding: "4px",
											opacity: 0.5,
										},
										"& input[type=date]": {
											fontSize: { xs: "16px", sm: "1rem" },
											cursor: "not-allowed",
										},
									}}
								/>
							</Grid>

							{formData.treatment_type !== "others" && (
								<Grid size={{ xs: 12, sm: 4 }}>
									<TextField
										fullWidth
										label="Number of Cycles Planned"
										name="no_of_cycles_planned"
										value={formData.no_of_cycles_planned}
										onChange={handleChange}
										InputLabelProps={{ shrink: true }}
									/>
								</Grid>
							)}

							{formData.treatment_type !== "others" && (
								<Grid size={{ xs: 12, sm: 4 }}>
									<TextField
										fullWidth
										label="Current Cycle"
										name="current_cycle"
										value={formData.current_cycle}
										onChange={handleChange}
										InputLabelProps={{ shrink: true }}
									/>
								</Grid>
							)}

							<Grid size={{ xs: 12, sm: 4 }}>
								<TextField
									fullWidth
									label="Hospital / Clinic"
									name="hospital"
									value={formData.hospital}
									InputProps={{
										readOnly: true,
									}}
									onChange={handleChange}
									InputLabelProps={{ shrink: true }}
								/>
							</Grid>

						</Grid>

					</Box>
				</DialogContent>
<DialogActions sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 1.5 }, gap: { xs: 1, sm: 0 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, '& > button': { width: { xs: '100%', sm: 'auto' } } }}>					<Button
						onClick={handleCloseDialog}
						sx={{
							px: { xs: 2.5, sm: 3 },
							py: { xs: 1.2, sm: 1 },
							borderRadius: '6px',
							textTransform: 'none',
							fontWeight: 600,
							color: '#64748b',
							border: '1px solid #e0e0e0',
							minHeight: { xs: '44px', sm: 'auto' },
							fontSize: { xs: '0.95rem', sm: '0.875rem' },
							'&:hover': { backgroundColor: '#f5f5f5' }
						}}
					>Cancel</Button>
					<Button
						variant="contained"
						onClick={handleSubmit}
						disabled={
							!formData.firstname?.trim() ||
							!formData.gender?.trim() ||
							!formData.phone?.trim() ||
							!formData.preferred_language?.trim() ||
							!formData.date_of_birth?.trim() ||
							!currentDate?.trim() ||
							!formData.email?.trim() ||
							!isValidEmail ||
							emailExists ||
							phoneExists ||
							(formData.phone && !/^[6-9]\d{9}$/.test(formData.phone)) ||
							!formData.assigned_doctor
						}
						sx={{
							background: 'linear-gradient(135deg, #4a90e2 0%, #357abd 100%)',
							px: { xs: 2.5, sm: 3 },
							py: { xs: 1.2, sm: 1 },
							minHeight: { xs: '44px', sm: 'auto' },
							fontSize: { xs: '0.95rem', sm: '0.875rem' },
							'&.Mui-disabled': {
								background: 'linear-gradient(135deg, #b0b0b0 0%, #9e9e9e 100%)',
								color: '#fff',
								cursor: 'not-allowed',
							},
						}}
					>
						{selectedTemplate ? 'Update' : 'Create'} Patient
					</Button>

				</DialogActions>
			</Dialog>

			<Dialog
				open={openHistory} onClose={() => setOpenHistory(false)}
				maxWidth="lg"
				fullWidth
				fullScreen={isMobile}
				sx={{
					'& .MuiDialog-paper': {
						margin: { xs: 0, sm: 2 },
						maxHeight: { xs: '100%', sm: 'calc(100% - 14px)' }
					}
				}}
			>
				<DialogTitle>
					<Box sx={{ px: { xs: 1, sm: 2 }, pt: 0, display: "flex", justifyContent: "space-between", }}>
						<Typography
							variant="h6"
							fontWeight={400}
							sx={{
								color: "#1f2937",
								fontSize: { xs: '1.1rem', sm: '1.25rem' }
							}}
						>
							Subscriber Details
						</Typography>
					</Box>
					<Box sx={{
						background: '#f8f9ff',
						px: { xs: 2, sm: 3 },
						py: { xs: 2, sm: 1 },
						borderRadius: { xs: '8px', sm: '10px' },
					}}>
						<Box display="flex" justifyContent="space-between" flexWrap={{ xs: 'wrap', sm: 'nowrap' }} gap={{ xs: 2, sm: 0 }}>
							<Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" flex="1" minWidth={{ xs: '100%', sm: 'auto' }}>
								<Typography
									variant="h6"
									sx={{
										color: '#1a1238',
										fontSize: { xs: '1rem', sm: '1.25rem' },
										display: 'flex',
										alignItems: 'center',
										gap: 1,
										flexWrap: 'wrap'
									}}
								>
									<img
										src={
											patient?.gender?.toLowerCase() === "male"
												? "/man.png"
												: patient?.gender?.toLowerCase() === "female"
													? "/woman.png"
													: userLogo
										}
										alt="gender-icon"
										style={{ width: 24, height: 24, flexShrink: 0 }}
									/>
									<span style={{ wordBreak: 'break-word' }}>
										{patient?.firstname} {patient?.lastname} |  Age: {patient?.age || 'N/A'}
									</span>
								</Typography>
							</Box>

							<Button
								variant="contained"
								onClick={() => {
									openEditPatient(patient);
									setOpenHistory(false);
								}}
								sx={{
									bgcolor: "#4A90E2",
									"&:hover": { bgcolor: "#357ABD" },
									borderRadius: 2,
									px: { xs: 2, sm: 2.5 },
									py: { xs: 0.8, sm: 1 },
									textTransform: "none",
									fontWeight: 600,
									fontSize: { xs: '0.85rem', sm: '0.875rem' },
									boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
									whiteSpace: "nowrap",
									minWidth: 'auto',
									height: { xs: 36, sm: 'auto' },
									flex: { xs: '0 0 auto', sm: '0 0 auto' },
									mt: { xs: 2, sm: 0 },
									width: { xs: '100%', sm: 'auto' }
								}}
							>
								Edit Patient
							</Button>

						</Box>

						<Typography sx={{
							color: '#666',
							fontSize: { xs: '0.85rem', sm: '1rem' },
							lineHeight: { xs: 1.6, sm: 1.5 },
							mt: 0,
							wordBreak: 'break-word',
							overflowWrap: 'break-word'
						}}>
							Email: {patient?.email || 'N/A'} | Phone: {patient?.phone || 'N/A'} | Preferred Language: {displayLanguage || 'N/A'}
						</Typography>

						<Typography sx={{
							color: '#666',
							fontSize: { xs: '0.85rem', sm: '1rem' },
							lineHeight: { xs: 1.6, sm: 1.5 },
							mt: 0.5,
							wordBreak: 'break-word',
							overflowWrap: 'break-word'
						}}>
							Assigned Doctor: {patient?.assigned_doctor_name || 'N/A'} | Diagnosis: {patient?.diagnosis || 'N/A'}
						</Typography>

						<Typography sx={{
							color: '#666',
							fontSize: { xs: '0.85rem', sm: '1rem' },
							lineHeight: { xs: 1.6, sm: 1.5 },
							mt: 0.5,
							wordBreak: 'break-word',
							overflowWrap: 'break-word'
						}}>
							Created At: {formatDateTime(patient?.created_at) || 'N/A'}
						</Typography>

						<Box
							sx={{
								px: 2,
								py: 1,
								border: '1px solid #e5e7eb',
								borderRadius: '10px',
								backgroundColor: '#f9fafb',
								mt: 1,
								overflow: 'auto'
							}}
						>
							<Typography sx={{
								color: '#666',
								fontSize: { xs: '0.85rem', sm: '1rem' },
								lineHeight: { xs: 1.6, sm: 1.5 },
								wordBreak: 'break-word',
								overflowWrap: 'break-word'
							}}>
								Medical Information — Primary Diagnosis: {patient?.primary_diagnosis ?? 'N/A'} |
								Stage: {patient?.stage ?? 'N/A'} |
								Date of Diagnosis: {patient?.date_of_diagnosis ? new Date(patient.date_of_diagnosis).toLocaleDateString() : ''}  |
								Treatment Type: {patient?.treatment_type ?? 'N/A'} |
								Treatment Start Date: {patient?.treatment_start_date ? new Date(patient.treatment_start_date).toLocaleDateString() : ''} |

								No of cycles planned: {patient?.no_of_cycles_planned ?? 'N/A'} |
								Current Cycle: {patient?.current_cycle ?? 'N/A'} |
								Hospital / Clinic: {patient?.hospital ?? 'N/A'}
							</Typography>
						</Box>

						<Typography sx={{
							color: '#666',
							mt: 1,
							fontSize: { xs: '0.85rem', sm: '1rem' },
							lineHeight: { xs: 1.6, sm: 1.5 },
							wordBreak: 'break-word',
							overflowWrap: 'break-word'
						}}>
							Pre-HADS Scores — Anxiety: {patient?.anxiety_score ?? 'N/A'}/21 |
							Depression: {patient?.depression_score ?? 'N/A'}/21 |
							Treatment Cycle: {patient?.treatment_cycle + 1}
						</Typography>
					</Box>
				</DialogTitle>

				{patient?.treatment_cycle > 0 && (
					<>
						<Typography
							variant="h6"
							fontWeight={400}
							sx={{
								color: "#1f2937",
								fontSize: { xs: '1.1rem', sm: '1.25rem' },
								paddingLeft: 4,
							}}
						>
							Treatment History
						</Typography>

						<DialogContent sx={{ padding: 4, paddingTop: 0.5, }}>
							{sessionHistory?.length === 0 ? (
								<Typography>No history found.</Typography>
							) : (
								sessionHistory?.map((item) => (
									<Box key={item.id}>
										{/* History Box */}
										<Box
											sx={{
												px: 2,
												py: 1,
												mb: 1,
												borderRadius: 2,
												boxShadow: expandedId === item.id ? 3 : 1,
												cursor: "pointer",
												background: expandedId === item.id ? "#e3f2fd" : "white",
												border: "1px solid",
												borderColor: expandedId === item.id ? "#90caf9" : "#e0e0e0",
												transition: "all 0.3s ease",
												"&:hover": {
													boxShadow: 3,
													backgroundColor: "#f5f5f5",
												},
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
											}}
											onClick={() => {
												setExpandedId(expandedId === item.id ? null : item.id);
												setSessionId(item.id); // Fetch details
											}}
										>
											<Box display="flex" alignItems="center" gap={2}>
												<Typography variant="subtitle1" sx={{ fontSize: "0.85rem" }}>
													Treatment Cycle - {item.treatment_cycle}
												</Typography>
											</Box>

											<Box textAlign="right">
												<Typography variant="body2" color="textSecondary">
													{formatDateTime(item.updated_at || item.created_at)}

												</Typography>
											</Box>
										</Box>

										{/* DROPDOWN CONTENT */}
										{expandedId === item.id && (
											<Box sx={{ p: 2, background: "#fafafa", borderRadius: "12px", mt: 1 }}>

												{loadingDetails ? (
													<Typography>Loading...</Typography>
												) : sessionDetails ? (
													<>

														{/* ⬅ NEW: Customer Info */}
														<Box
															sx={{
																display: "grid",
																gridTemplateColumns: {
																	xs: "1fr", // 1 column on mobile
																	sm: "repeat(2, 1fr)", // 2 columns on tablets
																	md: "repeat(4, 1fr)", // 4 columns on desktop
																},
																gap: 2,
																mb: 1,
															}}
														>
															<Box>
																<Typography variant="subtitle2" color="textSecondary">
																	Preferred Language
																</Typography>
																<Typography variant="body1">{displayLanguage || "-"}</Typography>
															</Box>

															<Box>
																<Typography variant="subtitle2" color="textSecondary">
																	Treatment Arm
																</Typography>
																<Typography variant="body1">
																	{sessionDetails?.treatment_arm === "test"
																		? "Test"
																		: sessionDetails?.treatment_arm === "control"
																			? "Control"
																			: "-"}
																</Typography>
															</Box>

															<Box>
																<Typography variant="subtitle2" color="textSecondary">
																	Status
																</Typography>
																<Chip
																	label={camelStatus}
																	size="small"   // ⬅ reduces chip size
																	sx={{
																		backgroundColor: chipColor,
																		color: "#fff",
																		fontWeight: 600,
																		textTransform: "capitalize",
																		borderRadius: "4px",   // ⬅ almost no curve (Chip requires minimum rounding)
																		px: 1,                 // reduce horizontal padding
																		height: 22             // compact height
																	}}
																/>


															</Box>

															<Box>
																	{camelStatus?.toLowerCase() === "completed" &&
																		isLatestCompletedPatientCycle &&
																		!hasPostVitals(sessionDetails) && (
																		<Button
																			variant="contained"
																			size="small"
																			sx={{
																				bgcolor: "#4A90E2",
																				"&:hover": { bgcolor: "#357ABD" },
																				textTransform: "none",
																				borderRadius: "4px",
																				fontWeight: 500,
																				mt: 2.5,
																				px: 1.2,
																				py: 0.5,
																				fontSize: "0.65rem",
																				minHeight: "20px",
																			}}
																			onClick={() => setOpenPostVital(true)}
																		>
																			Post Vital
																		</Button>
																	)}
															</Box>

														</Box>

														{/* Existing dynamic fields */}
														<Box
															sx={{
																display: "grid",
																gridTemplateColumns: {
																	xs: "1fr",
																	sm: "repeat(2, 1fr)",
																	md: "repeat(4, 1fr)",
																},
																gap: 2,
															}}
														>
															{fields.map((field, index) => (
																<Box
																	key={index}
																	sx={{
																		gridColumn:
																			field.label === "Suggested Care Soundscape" ||
																				field.label === "Assessment Type" ||
																				field.label === "Post Vital"
																				? "1 / -1"
																				: "auto",
																	}}
																>
																	<Typography variant="subtitle2" color="textSecondary">
																		{field.label}
																	</Typography>
																	<Typography variant="body1">{field.value}</Typography>
																</Box>
															))}
														</Box>

													</>
												) : (
													<Typography>No details available</Typography>
												)}
											</Box>
										)}

									</Box>
								))
							)}
						</DialogContent>
					</>
				)}

				<DialogActions sx={{ flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 }, '& > button': { width: { xs: '100%', sm: 'auto' } }, px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 1.5 } }}>

					<Button
						onClick={() =>
							setOpenHistory(false)
						}
						sx={{
							px: { xs: 2.5, sm: 3 },
							py: { xs: 1.2, sm: 1 },
							borderRadius: '6px',
							textTransform: 'none',
							fontWeight: 600,
							color: '#64748b',
							border: '1px solid #e0e0e0',
							minHeight: { xs: '44px', sm: 'auto' },
							fontSize: { xs: '0.95rem', sm: '0.875rem' },
							'&:hover': { backgroundColor: '#f5f5f5' }
						}}
					>Close</Button>
				</DialogActions>
			</Dialog>

<Dialog open={openPostVital} onClose={handleClosePostVital} fullWidth maxWidth="sm" fullScreen={isMobile}>				<DialogTitle>Post Vital</DialogTitle>

				<DialogContent>
					{/* Blood Pressure */}
					<Box sx={{ mb: 2 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
							<Typography
								variant="subtitle1"
								sx={{ color: '#374151', fontWeight: 500, fontSize: { xs: '0.95rem', sm: '1rem' } }}
							>
								Blood Pressure *
							</Typography>
							{formData.systolic && formData.diastolic && (
								<Box
									sx={{
										mt: 1,
										p: 1.5,
										borderRadius: '8px',
										backgroundColor: (() => {
											const sys = Number(formData.systolic);
											const dia = Number(formData.diastolic);
											if (sys < 90 || dia < 60) return '#dbeafe';
											if (sys < 120 && dia < 80) return '#dcfce7';
											if (sys < 130 && dia < 80) return '#fef3c7';
											if (sys < 140 || dia < 90) return '#fed7aa';
											return '#fecaca';
										})(),
										display: 'flex',
										alignItems: 'center',
										gap: 1,
									}}
								>
									<Box
										sx={{
											width: 8,
											height: 8,
											borderRadius: '50%',
											backgroundColor: (() => {
												const sys = Number(formData.systolic);
												const dia = Number(formData.diastolic);
												if (sys < 90 || dia < 60) return '#2563eb';
												if (sys < 120 && dia < 80) return '#16a34a';
												if (sys < 130 && dia < 80) return '#ca8a04';
												if (sys < 140 || dia < 90) return '#ea580c';
												return '#dc2626';
											})(),
										}}
									/>
									<Typography
										variant="caption"
										sx={{
											fontWeight: 600,
											fontSize: '0.75rem',
											color: (() => {
												const sys = Number(formData.systolic);
												const dia = Number(formData.diastolic);
												if (sys < 90 || dia < 60) return '#1e40af';
												if (sys < 120 && dia < 80) return '#166534';
												if (sys < 130 && dia < 80) return '#854d0e';
												if (sys < 140 || dia < 90) return '#9a3412';
												return '#991b1b';
											})(),
										}}
									>
										{(() => {
											const sys = Number(formData.systolic);
											const dia = Number(formData.diastolic);
											if (sys < 90 || dia < 60) return 'Low Blood Pressure (Hypotension)';
											if (sys < 120 && dia < 80) return 'Normal Blood Pressure';
											if (sys < 130 && dia < 80) return 'Elevated Blood Pressure';
											if (sys < 140 || dia < 90) return 'Hypertension Stage 1';
											return 'Hypertension Stage 2';
										})()}
									</Typography>
								</Box>
							)}
						</Box>

						{/* Inputs */}
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1.5,
								p: 2,
								border: '2px solid #e5e7eb',
								borderRadius: '12px',
								backgroundColor: '#f9fafb',
								transition: 'all 0.2s ease',
								position: 'relative',
								overflow: 'hidden',
							}}
						>
							{/* Systolic */}
							<Box sx={{ flex: 1, textAlign: 'center' }}>
								<Typography
									variant="caption"
									sx={{
										color: '#6b7280',
										fontWeight: 600,
										fontSize: '0.7rem',
										textTransform: 'uppercase',
										letterSpacing: '0.5px',
										display: 'block',
										mb: 0.5,
									}}
								>
									Systolic
								</Typography>
								<TextField
									name="systolic"
									type="number"
									placeholder="120"
									value={formData.systolic || ''}
									onChange={(e) => {
										const systolic = e.target.value;
										const diastolic = formData.diastolic || '';
										setFormData((prev) => ({
											...prev,
											systolic,
											bloodPressure: systolic && diastolic ? `${systolic}/${diastolic}` : '',
										}));
									}}
									variant="standard"
									InputProps={{
										disableUnderline: true,
										inputProps: { min: 0, max: 300, style: { textAlign: 'center' } },
									}}
									sx={{
'& input': { fontSize: { xs: '1.2rem', sm: '1.5rem' }, fontWeight: 600, color: '#1f2937', padding: 0 },										'& input::placeholder': { color: '#d1d5db', opacity: 1 },
									}}
								/>
							</Box>

							<Box sx={{ fontSize: '2rem', fontWeight: 300, color: '#9ca3af', lineHeight: 1, pb: 0.5 }}>/</Box>

							{/* Diastolic */}
							<Box sx={{ flex: 1, textAlign: 'center' }}>
								<Typography
									variant="caption"
									sx={{
										color: '#6b7280',
										fontWeight: 600,
										fontSize: '0.7rem',
										textTransform: 'uppercase',
										letterSpacing: '0.5px',
										display: 'block',
										mb: 0.5,
									}}
								>
									Diastolic
								</Typography>
								<TextField
									name="diastolic"
									type="number"
									placeholder="80"
									value={formData.diastolic || ''}
									onChange={(e) => {
										const diastolic = e.target.value;
										const systolic = formData.systolic || '';
										setFormData((prev) => ({
											...prev,
											diastolic,
											bloodPressure: systolic && diastolic ? `${systolic}/${diastolic}` : '',
										}));
									}}
									variant="standard"
									InputProps={{
										disableUnderline: true,
										inputProps: { min: 0, max: 200, style: { textAlign: 'center' } },
									}}
									sx={{
										'& input': { fontSize: '1.5rem', fontWeight: 600, color: '#1f2937', padding: 0 },
										'& input::placeholder': { color: '#d1d5db', opacity: 1 },
									}}
								/>
							</Box>

							{/* Unit */}
							<Box
								sx={{
									bgcolor: '#3b82f6',
									color: 'white',
									px: 1.5,
									py: 1,
									borderRadius: '8px',
									fontWeight: 600,
									fontSize: '0.85rem',
									minWidth: '60px',
									textAlign: 'center',
								}}
							>
								mmHg
							</Box>
						</Box>
					</Box>

					{/* Heart Rate */}
					<Box sx={{ mb: 2 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
							<Typography variant="subtitle1" sx={{ color: '#374151', fontWeight: 500, fontSize: { xs: '0.95rem', sm: '1rem' } }}>
								Heart Rate *
							</Typography>
							{formData.heartRate && (
								<Box
									sx={{
										mt: 1,
										p: 1.5,
										borderRadius: '8px',
										backgroundColor: (() => {
											const hr = Number(formData.heartRate);
											if (hr >= 60 && hr <= 100) return '#dcfce7';
											if (hr > 100 && hr <= 120) return '#fef3c7';
											if ((hr >= 40 && hr < 60) || (hr > 120 && hr <= 150)) return '#fed7aa';
											return '#fecaca';
										})(),
										display: 'flex',
										alignItems: 'center',
										gap: 1,
									}}
								>
									<Box
										sx={{
											width: 8,
											height: 8,
											borderRadius: '50%',
											backgroundColor: (() => {
												const hr = Number(formData.heartRate);
												if (hr >= 60 && hr <= 100) return '#16a34a';
												if (hr > 100 && hr <= 120) return '#ca8a04';
												if ((hr >= 40 && hr < 60) || (hr > 120 && hr <= 150)) return '#ea580c';
												return '#dc2626';
											})(),
										}}
									/>
									<Typography
										variant="caption"
										sx={{
											fontWeight: 600,
											fontSize: '0.75rem',
											color: (() => {
												const hr = Number(formData.heartRate);
												if (hr >= 60 && hr <= 100) return '#166534';
												if (hr > 100 && hr <= 120) return '#854d0e';
												if ((hr >= 40 && hr < 60) || (hr > 120 && hr <= 150)) return '#9a3412';
												return '#991b1b';
											})(),
										}}
									>
										{(() => {
											const hr = Number(formData.heartRate);
											if (hr >= 60 && hr <= 100) return 'Normal Heart Rate';
											if (hr > 100 && hr <= 120) return 'Slightly Elevated';
											if (hr > 120) return 'Tachycardia (High)';
											if (hr >= 40 && hr < 60) return 'Bradycardia (Low)';
											return 'Critical - Seek Medical Attention';
										})()}
									</Typography>
								</Box>
							)}
						</Box>

						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 2,
								p: 2,
								border: '2px solid #e5e7eb',
								borderRadius: '12px',
								backgroundColor: '#f9fafb',
								transition: 'all 0.2s ease',
								position: 'relative',
								overflow: 'hidden',
							}}
						>
							<Box sx={{ flex: 1, textAlign: 'center' }}>
								<Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, fontSize: '0.7rem', mb: 0.5 }}>
									BPM
								</Typography>
								<TextField
									name="heartRate"
									type="number"
									placeholder="72"
									value={formData.heartRate || ''}
									onChange={(e) => setFormData(prev => ({ ...prev, heartRate: e.target.value }))}
									variant="standard"
									InputProps={{ disableUnderline: true, inputProps: { min: 0, max: 250, style: { textAlign: 'center' } } }}
sx={{ '& input': { fontSize: { xs: '1.5rem', sm: '2rem' }, fontWeight: 700, color: '#1f2937', padding: 0 } }}								/>
							</Box>

							<Box
								sx={{
									bgcolor: '#f43f5e',
									color: 'white',
									px: 1.5,
									py: 1,
									borderRadius: '8px',
									fontWeight: 600,
									fontSize: '0.85rem',
									minWidth: '70px',
									textAlign: 'center',
								}}
							>
								beats/min
							</Box>
						</Box>
					</Box>

					{/* Pain Scale */}
					<Box sx={{ mb: 3, mt: 1 }}>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
							<Typography variant="subtitle1" sx={{ color: '#374151', fontWeight: 500, fontSize: { xs: '0.95rem', sm: '1rem' } }}>
								Pain Scale *
							</Typography>
							<Box
								sx={{
									bgcolor: formData.painScale ? `hsl(${120 - formData.painScale * 12}, 70%, 50%)` : '#e5e7eb',
									color: 'white',
									px: 2,
									py: 0.5,
									borderRadius: '12px',
									fontWeight: 600,
									fontSize: '1.1rem',
									minWidth: '50px',
									textAlign: 'center',
									boxShadow: formData.painScale
										? `0 4px 12px hsla(${120 - formData.painScale * 12}, 70%, 50%, 0.4)`
										: 'none',
									transition: 'all 0.3s ease',
								}}
							>
								{formData.painScale || 0}
							</Box>
						</Box>

						<Slider
							value={Number(formData.painScale) || 0}
							onChange={(e, value) => setFormData(prev => ({ ...prev, painScale: value }))}
							min={0}
							max={10}
							step={1}
							marks={[{ value: 0 }, { value: 2 }, { value: 4 }, { value: 6 }, { value: 8 }, { value: 10 }]}
							sx={{
								'& .MuiSlider-thumb': { width: 24, height: 24, backgroundColor: '#fff', border: '3px solid currentColor' },
								'& .MuiSlider-track': {
									height: 8,
									borderRadius: 4,
									background: formData.painScale ? `linear-gradient(90deg, #10b981 0%, hsl(${120 - formData.painScale * 12}, 70%, 50%) 100%)` : '#10b981',
								},
								'& .MuiSlider-rail': { height: 8, borderRadius: 4, backgroundColor: '#e5e7eb' },
								color: formData.painScale ? `hsl(${120 - formData.painScale * 12}, 70%, 50%)` : '#10b981',
							}}
						/>

						<Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, px: 0.5 }}>
							<Typography variant="caption" sx={{ color: '#10b981', fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
								No Pain
							</Typography>
							<Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
								Worst Pain
							</Typography>
						</Box>
					</Box>
				</DialogContent>

				<DialogActions sx={{ flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 }, '& > button': { width: { xs: '100%', sm: 'auto' } }, px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 1.5 } }}>

					<Button onClick={handleClosePostVital}>Cancel</Button>
					<Button
						variant="contained"
						onClick={async () => {
							try {
								if (!selectedCustomerId) {
									alert("No customer selected!");
									return;
								}

								// Prepare payload
								const payload = {
									session_detail_id: sessionId,
									blood_pressure: formData.bloodPressure, // combined systolic/diastolic
									heart_rate: Number(formData.heartRate),
									pain_scale: Number(formData.painScale),
								};

								console.log("Submitting Post Vital...", payload);

								// Call your service
								const result = await createCustomerPostvital(payload);

								console.log("Post Vital created successfully:", result);
								alert("Post Vital submitted successfully!");
								fetchSessionDetails(sessionId);

								handleClosePostVital();
							} catch (error) {
								console.error("Failed to submit Post Vital:", error);
								alert("Failed to submit Post Vital. Please try again.");
							}
						}}
					>
						Submit
					</Button>
				</DialogActions>


			</Dialog>



			<Dialog
				open={isInsightsComingSoonOpen}
				onClose={() => setIsInsightsComingSoonOpen(false)}
				PaperProps={{
					sx: {
						borderRadius: 3,
						p: { xs: 2, sm: 3 },
						minWidth: { xs: 280, sm: 400 },
						textAlign: 'center'
					}
				}}
			>
				<Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
					<Box
						sx={{
							width: 80,
							height: 80,
							borderRadius: '50%',
							background: 'linear-gradient(135deg, #4a6cf7 0%, #3b5bdb 100%)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							boxShadow: '0 8px 24px rgba(74, 108, 247, 0.3)'
						}}
					>
						<InsightsIcon sx={{ color: 'white', fontSize: 40 }} />
					</Box>
				</Box>

				<DialogTitle sx={{ textAlign: 'center', fontWeight: 700, fontSize: '1.5rem', pb: 1 }}>
					Coming Soon!
				</DialogTitle>

				<DialogContent>
					<DialogContentText sx={{ textAlign: 'center', fontSize: '1rem', color: 'text.secondary', mb: 2 }}>
						We're working hard to bring you personalized insights and analytics.
					</DialogContentText>
					<DialogContentText sx={{ textAlign: 'center', fontSize: '0.9rem', color: 'text.secondary' }}>
						Stay tuned for updates! 🚀
					</DialogContentText>
				</DialogContent>

				<DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
					<Button
						onClick={() => setIsInsightsComingSoonOpen(false)}
						variant="contained"
						sx={{
							px: 4,
							py: 1,
							borderRadius: 2,
							textTransform: 'none',
							fontWeight: 600,
							background: 'linear-gradient(135deg, #4a6cf7 0%, #3b5bdb 100%)',
							'&:hover': {
								background: 'linear-gradient(135deg, #3b5bdb 0%, #2f4bbd 100%)'
							}
						}}
					>
						Got it!
					</Button>
				</DialogActions>
			</Dialog>

			<style jsx>{`
		.line-clamp-2 {
		  display: -webkit-box;
		  -webkit-line-clamp: 2;
		  -webkit-box-orient: vertical;
		  overflow: hidden;
		}

		@keyframes spin {
		  from { transform: rotate(0deg); }
		  to { transform: rotate(360deg); }
		}

		.animate-spin {
		  animation: spin 1s linear infinite;
		}

		.animate-pulse {
		  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
		}

		@keyframes pulse {
		  0%, 100% { opacity: 1; }
		  50% { opacity: .5; }
		}
	  `}</style>
		</Box >
	);
};

export default Patients;
